import React, { useEffect, useMemo, useState } from 'react'
import { format, parseISO } from 'date-fns'
import {
  Header,
  Loadable,
  Pagination,
  TableHead,
  useApiGet,
  useModal,
  useSortableTable,
  varClass,
} from '../shared'
import {
  IAnomaliesV2Suppression,
  IAnomaliesV2SuppressionsResponse,
} from '../shared/types'
import { deleteSuppression } from './api'
import { ConfirmModal } from './ConfirmModal'
import { SUPPRESSION_METRIC_LABELS } from './SuppressModal'

// Full-page suppressions admin view. Lives as the third tab on /anomalies.
// Patterns mirror RejectionReview: client-side sort + pagination over the
// full dataset, the same segmented-control + filter pill aesthetic, and a
// matching pagination footer (count left, controls right).

type StatusFilter = 'active' | 'all'

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  active: 'Active only',
  all: 'Include expired & cancelled',
}

const isActive = (row: IAnomaliesV2Suppression): boolean => {
  if (row.deletedAt) return false
  if (!row.expiresAt) return true
  try {
    return parseISO(row.expiresAt).getTime() > Date.now()
  } catch {
    return true
  }
}

const statusFor = (row: IAnomaliesV2Suppression): 'active' | 'expired' | 'cancelled' => {
  if (row.deletedAt) return 'cancelled'
  if (isActive(row)) return 'active'
  return 'expired'
}

const safeFormat = (
  value: string | null | undefined,
  pattern: string
): string => {
  if (!value) return '—'
  try {
    return format(parseISO(value), pattern)
  } catch {
    return value
  }
}

// Human label for what a suppression row targets. null (all daily alerts) is
// the pre-scoping behavior; 'rejections' is the monthly-review-only scope.
const scopeLabel = (row: IAnomaliesV2Suppression): string => {
  if (!row.primaryMetric) return 'All alerts'
  if (row.primaryMetric === 'rejections') return 'Rejection review'
  return `${SUPPRESSION_METRIC_LABELS[row.primaryMetric]} only`
}

// Render the author's name when the backend ships one; fall back to a short
// UUID prefix for older records or system-created suppressions.
const displayAuthor = (row: IAnomaliesV2Suppression): string => {
  const first = (row.createdByUserFirstname || '').trim()
  const last = (row.createdByUserLastname || '').trim()
  const full = [first, last].filter(Boolean).join(' ')
  if (full) return full
  if (!row.createdByUserUuid) return '—'
  return row.createdByUserUuid.length > 8
    ? `${row.createdByUserUuid.slice(0, 8)}…`
    : row.createdByUserUuid
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]

export const SuppressionsView = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(t)
  }, [searchInput])

  const [refetchKey, setRefetchKey] = useState(0)
  const [removing, setRemoving] = useState<string | null>(null)

  const params = useMemo(
    () => (statusFilter === 'all' ? { include_inactive: 'true' } : undefined),
    [statusFilter]
  )
  const data = useApiGet<IAnomaliesV2SuppressionsResponse>(
    '/anomalies/v2/suppressions',
    params,
    [statusFilter, refetchKey]
  )

  const rows = data?.suppressions || []

  const filtered = useMemo(() => {
    if (!appliedSearch) return rows
    const needle = appliedSearch.toLowerCase()
    return rows.filter((r) =>
      r.advertiserDomain.toLowerCase().includes(needle)
    )
  }, [rows, appliedSearch])

  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'createdAt',
    dir: 'desc',
  })

  const sortedRows = useMemo(() => {
    const sign = dir === 'desc' ? -1 : 1
    const get = (r: IAnomaliesV2Suppression): unknown => {
      switch (sort) {
        case 'advertiserDomain':
          return r.advertiserDomain
        case 'scope':
          return scopeLabel(r)
        case 'createdAt':
          return r.createdAt
        case 'expiresAt':
          // Indefinite suppressions never expire — sort them at the far end
          // of "desc" (no end date is the strongest signal).
          return r.expiresAt ?? (dir === 'desc' ? '9999-12-31' : '')
        case 'status':
          return statusFor(r)
        case 'createdBy':
          return displayAuthor(r).toLowerCase()
        default:
          return r.createdAt
      }
    }
    return [...filtered].sort((a, b) => {
      const av = get(a)
      const bv = get(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
      return String(av).localeCompare(String(bv)) * sign
    })
  }, [filtered, sort, dir])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Reset to page 1 whenever filters or sort change so the user doesn't end
  // up on an empty page.
  useEffect(() => {
    setPage(1)
  }, [statusFilter, appliedSearch, sort, dir, pageSize])

  const total = sortedRows.length
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  )

  const { showModal, hideModal } = useModal()

  const handleRemove = async (uuid: string) => {
    setRemoving(uuid)
    try {
      await deleteSuppression(uuid)
      setRefetchKey((k) => k + 1)
    } finally {
      setRemoving(null)
    }
  }

  // What comes back when this suppression is lifted — scope-aware so the
  // operator knows exactly what they're re-surfacing.
  const removalConsequence = (row: IAnomaliesV2Suppression): string => {
    if (row.primaryMetric === 'rejections') {
      return 'The advertiser will reappear in the rejection review.'
    }
    if (row.primaryMetric) {
      return `${SUPPRESSION_METRIC_LABELS[row.primaryMetric]} alerts will reappear in daily triage.`
    }
    return 'Its daily alerts will reappear in triage.'
  }

  const confirmRemove = (row: IAnomaliesV2Suppression) => {
    showModal(
      <ConfirmModal
        title="Remove suppression"
        body={
          <>
            Remove the <b>{scopeLabel(row)}</b> suppression for{' '}
            <b>{row.advertiserDomain}</b>? {removalConsequence(row)}
          </>
        }
        confirmLabel="Remove"
        hideModal={hideModal}
        onConfirm={() => handleRemove(row.uuid)}
      />
    )
  }

  const headProps = { sort, dir, toggleSort }

  return (
    <div className="anomalies-page">
      <Header label="Suppressions" />

      <div className="card anomalies-rejection-review-filters m-t-4">
        <div className="card-body">
          <div
            className="row row-center row-space-between"
            style={{ gap: 12, flexWrap: 'wrap' }}
          >
            <div className="row row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="control" style={{ width: 240 }}>
                <input
                  type="text"
                  placeholder="Search advertiser…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div className="anomalies-rejection-review-view-toggle">
              {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={varClass({
                    'anomalies-rejection-review-view-toggle-btn': true,
                    active: statusFilter === s,
                  })}
                  onClick={() => setStatusFilter(s)}
                >
                  {STATUS_FILTER_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card m-t-4">
        <div className="card-body">
          <Loadable data={data}>
            {sortedRows.length === 0 && data ? (
              <div className="text-light text-center p-y-10">
                {appliedSearch
                  ? `No suppressions match "${appliedSearch}".`
                  : statusFilter === 'all'
                  ? 'No suppressions in the history.'
                  : 'No active suppressions.'}
              </div>
            ) : (
              <div className="table-container-overflow">
                <table className="table table-bordered table-hoverable table-sortable anomalies-rejection-review-table">
                  <thead>
                    <tr>
                      <TableHead value="advertiserDomain" label="Advertiser" {...headProps} />
                      <TableHead value="scope" label="Scope" {...headProps} />
                      <TableHead value="status" label="Status" {...headProps} />
                      <TableHead value="createdAt" label="Suppressed" desc {...headProps} />
                      <TableHead value="expiresAt" label="Expires" desc {...headProps} />
                      <TableHead value="createdBy" label="By" {...headProps} />
                      <th className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row) => (
                      <SuppressionRow
                        key={row.uuid}
                        row={row}
                        removing={removing === row.uuid}
                        onRemove={() => confirmRemove(row)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Loadable>

          {total > 0 && (
            <div className="row row-space-between row-center m-t-3">
              <div className="text-light text-small">
                {total} suppression{total === 1 ? '' : 's'}
              </div>
              <div className="row row-center" style={{ gap: 12 }}>
                <Pagination page={page} setPage={setPage} maxPage={totalPages} />
                <label
                  className="text-light text-small row row-center"
                  style={{ gap: 6 }}
                >
                  Page size
                  <select
                    className="anomalies-page-size-select"
                    value={pageSize}
                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const SuppressionRow = (props: {
  row: IAnomaliesV2Suppression
  removing: boolean
  onRemove: () => void
}) => {
  const { row, removing, onRemove } = props
  const status = statusFor(row)
  return (
    <tr
      className={varClass({
        'anomalies-suppression-row-inactive': status !== 'active',
      })}
    >
      <td>
        <div className="text-dark text-bold">{row.advertiserDomain}</div>
        {row.reason && (
          <div className="anomalies-suppression-reason">{row.reason}</div>
        )}
      </td>
      <td>
        <ScopeChip row={row} />
      </td>
      <td>
        <StatusBadge status={status} />
      </td>
      <td>{safeFormat(row.createdAt, 'MMM d, yyyy')}</td>
      <td>
        {row.expiresAt
          ? safeFormat(row.expiresAt, 'MMM d, yyyy')
          : <span className="text-light">Indefinite</span>}
      </td>
      <td title={row.createdByUserUuid || undefined}>
        {displayAuthor(row)}
      </td>
      <td className="text-right">
        {status === 'active' ? (
          <button
            type="button"
            className="anomalies-faq-toggle"
            onClick={onRemove}
            disabled={removing}
          >
            {removing ? 'Removing…' : 'Remove'}
          </button>
        ) : (
          <span className="text-light text-small">—</span>
        )}
      </td>
    </tr>
  )
}

// One chip per row — a suppression only ever has one scope. "All alerts" is
// muted (the default, most common case); metric scopes and the rejection-
// review scope get their own tints so mixed lists scan quickly.
const ScopeChip = (props: { row: IAnomaliesV2Suppression }) => {
  const scope = props.row.primaryMetric
  return (
    <span
      className={varClass({
        'anomalies-suppression-scope-chip': true,
        'anomalies-suppression-scope-chip-all': !scope,
        'anomalies-suppression-scope-chip-metric':
          !!scope && scope !== 'rejections',
        'anomalies-suppression-scope-chip-rejections': scope === 'rejections',
      })}
    >
      {scopeLabel(props.row)}
    </span>
  )
}

const StatusBadge = (props: { status: 'active' | 'expired' | 'cancelled' }) => {
  const labels: Record<typeof props.status, string> = {
    active: 'Active',
    expired: 'Expired',
    cancelled: 'Cancelled',
  }
  return (
    <span
      className={varClass({
        'anomalies-suppression-badge': true,
        [`anomalies-suppression-badge-${props.status}`]: true,
      })}
    >
      {labels[props.status]}
    </span>
  )
}
